import { ref, computed, readonly } from 'vue';
import { useSavedChartsStore } from '@/stores/savedChartsStore';
import { useCookies } from '@/composables/useCookies';
import { INVALID_CHART_STYLE_RECID } from '@/composables/isValidChartStyleRecid';
import { parseChartStyle } from '@/composables/parseChartStyle';
import { isValidNumber } from '@/utils/isValidNumber';
import { throttle } from '@/utils/throttle';
import { wait } from '@/utils/wait';
import { isInteractiveHtmlElement } from '@/utils/isInteractiveHtmlElement';
import { isJsonLayerResponse } from '@/interfaces/jsonLayerResponse';
import { isFetchDefaultChartStyleResponse } from '@/interfaces/json-layer-responses/jsonLayerFetchDefaultChartStyleResponse';
import { MEMBER_TYPES, isMemberType } from '@/enums/member-types';

const isAuthenticatingMember = ref(false);
const isMemberAuthenticationComplete = ref(false);
const memberAuthenticationCallbacks = [];
let hasInitialized = false;

const rawMemberData = ref({});

const OPTIONS_PLAY_ADD_ON_DIGITAL_PRODUCT_ID = '42';
const hasOptionsPlayAddOn = ref(false);

const isLoggedIn = computed(() => {
  return (
    !!rawMemberData.value &&
    isValidChartCode(rawMemberData.value.chartCode)
  );
});

const chartCode = computed(() => {
  if (!isLoggedIn.value) {
    return '';
  }

  return formatRawChartCode(rawMemberData.value.chartCode);
});

const memberName = computed(() => {
  if (!isLoggedIn.value) {
    return '';
  }

  if (
    typeof rawMemberData.value.firstName === 'string' &&
    rawMemberData.value.firstName.trim()
  ) {
    return rawMemberData.value.firstName.trim();
  }

  if (
    typeof rawMemberData.value.userId === 'string' &&
    rawMemberData.value.userId.trim()
  ) {
    return rawMemberData.value.userId.trim();
  }

  if (
    typeof rawMemberData.value.userID === 'string' &&
    rawMemberData.value.userID.trim()
  ) {
    return rawMemberData.value.userID.trim();
  }

  return '';
});

const memberLastName = computed(() => {
  if (!isLoggedIn.value) {
    return '';
  }

  if (
    typeof rawMemberData.value.lastName === 'string' &&
    rawMemberData.value.lastName.trim()
  ) {
    return rawMemberData.value.lastName.trim();
  }

  return '';
});

const memberFullName = computed(() => {
  if (!isLoggedIn.value) {
    return '';
  }

  if (
    typeof rawMemberData.value.fullName === 'string' &&
    rawMemberData.value.fullName.trim()
  ) {
    return rawMemberData.value.fullName.trim();
  }

  return '';
});

const memberEmail = computed(() => {
  if (!isLoggedIn.value) {
    return '';
  }

  if (
    typeof rawMemberData.value.userID === 'string' &&
    rawMemberData.value.userID.trim()
  ) {
    return rawMemberData.value.userID.trim();
  }

  return '';
});

const memberType = computed(() => {
  if (
    !isLoggedIn.value ||
    typeof rawMemberData.value.memberType !== 'string'
  ) {
    return MEMBER_TYPES.Free;
  }

  const rawMemberType = rawMemberData.value.memberType.replace(/\s/g, '').toUpperCase();

  return isMemberType(rawMemberType) ? rawMemberType : MEMBER_TYPES.Free;
});

const memberRT = computed(() => {
  if (
    !isLoggedIn.value ||
    typeof rawMemberData.value.memberRT !== 'string'
  ) {
    return '';
  }

  return rawMemberData.value.memberRT.replace(/\s/g, '').toUpperCase();
});

const isBasic = computed(() => {
  return memberType.value === MEMBER_TYPES.Basic;
});

const isExtra = computed(() => {
  return memberType.value === MEMBER_TYPES.Extra;
});

const isPro = computed(() => {
  return memberType.value === MEMBER_TYPES.Pro;
});

function isValidChartCode(possiblyValidChartCode) {
  return isValidNumber(possiblyValidChartCode, { isRequiredToBeInteger: true });
}

function formatRawChartCode(rawChartCode) {
  return `${Number(rawChartCode)}`;
}

function resetAuthenticationData() {
  rawMemberData.value = {};
  hasOptionsPlayAddOn.value = false;
}

function extractChartCodeFromLoginCookie(loginCookie) {
  if (typeof loginCookie !== 'string') {
    throw new Error(`Unable to extract chartCode from login cookie - loginCookie argument is not a string: ${typeof loginCookie}.`);
  }

  const loginCookieParts = loginCookie.replace(/\s/g, '').split('.');

  if (loginCookieParts.length < 2) {
    throw new Error(`Unable to extract chartCode from login cookie - loginCookie: ${loginCookie} string split on period does not have 2 or more distinct parts: ${loginCookieParts.length}.`);
  }

  const rawChartCode = loginCookieParts[1];

  if (!isValidChartCode(rawChartCode)) {
    throw new Error(`Unable to extract chartCode from login cookie - login cookie substring located between the first and second periods in the login cookie is not a valid chartCode: ${rawChartCode}.`);
  }

  return formatRawChartCode(rawChartCode);
}

function doesMemberHaveOptionsPlayAddOn(loginCookie) {
  if (typeof loginCookie !== 'string') {
    throw new Error(`Unable to determine if member has OptionsPlay add-on - loginCookie argument is not a string: ${typeof loginCookie}.`);
  }

  return loginCookie
    .replace(/\s/g, '')
    .split('.')
    .includes(OPTIONS_PLAY_ADD_ON_DIGITAL_PRODUCT_ID);
}

function hasLoginCookie() {
  const { getCookie } = useCookies();

  return !!getCookie('SCCLogin2');
}

async function fetchMemberDataAndDefaultChartStyle({ chartCode: memberChartCode }) {
  let responseJson;

  try {
    const response = await fetch('/json/api', {
      method: 'POST',
      body: new URLSearchParams({
        cmd: 'batch',
        requests: JSON.stringify([
          `json/api?cmd=get-member&chartCode=${memberChartCode}`,
          `json/api?cmd=get-default-chartstyle&chartCode=${memberChartCode}`
        ])
      }),
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    });

    responseJson = await response.json();

    window.sc3WorkbenchLogger.info(`get-member/get-default-chartstyle batch responseJson for chartCode: ${memberChartCode}.`, responseJson);
  } catch (e) {
    window.sc3WorkbenchLogger.error(`Failed to get member data and default ChartStyle for chartCode: ${memberChartCode}.`, e);

    throw new Error(`Failed to get member data and default ChartStyle for chartCode: ${memberChartCode}.`, { cause: e });
  }

  if (!responseJson) {
    throw new Error(`Failed to get member data and default ChartStyle for chartCode: ${memberChartCode} - responseJson is falsy: ${typeof responseJson} ${responseJson}.`);
  }

  if (typeof responseJson !== 'object') {
    throw new Error(`Failed to get member data and default ChartStyle for chartCode: ${memberChartCode} - responseJson is truthy but is not an object: ${typeof responseJson}.`);
  }

  if (typeof responseJson.success !== 'boolean') {
    throw new Error(`Failed to get member data and default ChartStyle for chartCode: ${memberChartCode} - responseJson.success is not a boolean: ${typeof responseJson.success}.`);
  }

  if (!responseJson.success) {
    throw new Error(`Failed to get member data and default ChartStyle for chartCode: ${memberChartCode} - responseJson.success is false.`);
  }

  if (!responseJson.responses) {
    throw new Error(`Failed to get member data and default ChartStyle for chartCode: ${memberChartCode} - responseJson.responses is falsy: ${typeof responseJson.responses} ${responseJson.responses}.`);
  }

  if (typeof responseJson.responses !== 'object') {
    throw new Error(`Failed to get member data and default ChartStyle for chartCode: ${memberChartCode} - responseJson.responses is truthy but is not an object: ${typeof responseJson.responses}.`);
  }

  if (!Array.isArray(responseJson.responses)) {
    throw new Error(`Failed to get member data and default ChartStyle for chartCode: ${memberChartCode} - responseJson.responses is a truthy object but is not an array.`);
  }

  if (!responseJson.responses.length) {
    throw new Error(`Failed to get member data and default ChartStyle for chartCode: ${memberChartCode} - responseJson.responses is an empty array.`);
  }

  if (!responseJson.responses[0]) {
    throw new Error(`Failed to get member data and default ChartStyle for chartCode: ${memberChartCode} - responseJson.responses[0] is falsy: ${typeof responseJson.responses[0]} ${responseJson.responses[0]}.`);
  }

  if (typeof responseJson.responses[0] !== 'object') {
    throw new Error(`Failed to get member data and default ChartStyle for chartCode: ${memberChartCode} - responseJson.responses[0] is truthy but is not an object: ${typeof responseJson.responses[0]}.`);
  }

  if (!responseJson.responses[0].member) {
    throw new Error(`Failed to get member data and default ChartStyle for chartCode: ${memberChartCode} - responseJson.responses[0].member is falsy: ${typeof responseJson.responses[0].member} ${responseJson.responses[0].member}.`);
  }

  if (typeof responseJson.responses[0].member !== 'object') {
    throw new Error(`Failed to get member data and default ChartStyle for chartCode: ${memberChartCode} - responseJson.responses[0].member is truthy but is not an object: ${typeof responseJson.responses[0].member}.`);
  }

  const relevantResponseData = {
    memberData: responseJson.responses[0].member,
    defaultChartStyle: null
  };

  const fetchDefaultChartStyleResponse = responseJson.responses[1];

  if (
    isJsonLayerResponse(fetchDefaultChartStyleResponse) &&
    fetchDefaultChartStyleResponse.success &&
    isFetchDefaultChartStyleResponse(fetchDefaultChartStyleResponse)
  ) {
    relevantResponseData.defaultChartStyle = parseChartStyle(fetchDefaultChartStyleResponse.chartStyle);
  }

  return relevantResponseData;
}

async function loginMember() {
  let responseJson;

  try {
    const response = await fetch('/json/api?cmd=login-member&src=sc3ui', {
      method: 'POST'
    });

    responseJson = await response.json();

    window.sc3WorkbenchLogger.info('login member responseJson.', responseJson);
  } catch (e) {
    window.sc3WorkbenchLogger.error('Failed to login member.', e);

    throw new Error('Failed to login member.', { cause: e });
  }

  if (!responseJson) {
    throw new Error(`Failed to login member - responseJson is falsy: ${typeof responseJson} ${responseJson}.`);
  }

  if (typeof responseJson !== 'object') {
    throw new Error(`Failed to login member - responseJson is truthy but is not an object: ${typeof responseJson}.`);
  }

  if (typeof responseJson.success !== 'boolean') {
    throw new Error(`Failed to login member - responseJson.success is not a boolean: ${typeof responseJson.success}.`);
  }

  return responseJson;
}

async function authenticateMember() {
  const savedChartsStore = useSavedChartsStore();

  if (isAuthenticatingMember.value || isMemberAuthenticationComplete.value) {
    return;
  }

  isAuthenticatingMember.value = true;

  resetAuthenticationData();

  try {
    window.sc3WorkbenchLogger.info('Attempting to login member...');

    if (!hasLoginCookie()) {
      throw new Error('Failed to authenticate member - login cookie is missing.');
    }

    const loginResponseJson = await loginMember();

    if (!loginResponseJson.success) {
      throw new Error(`Failed to login member - loginResponseJson.success is false.`);
    }

    const loginCookie = loginResponseJson.loginCookie;

    if (typeof loginCookie !== 'string') {
      throw new Error(`Failed to authenticate member - loginResponseJson.loginCookie is not a string: ${typeof loginCookie}.`);
    }

    if (!loginCookie.trim()) {
      throw new Error('Failed to authenticate member - trimmed loginResponseJson.loginCookie is an empty string.');
    }

    window.sc3WorkbenchLogger.info('Successfully confirmed that member is logged-in.');
    window.sc3WorkbenchLogger.info(`Attempting to extract chartCode from login cookie: ${loginCookie}...`);

    const memberChartCode = extractChartCodeFromLoginCookie(loginCookie);

    window.sc3WorkbenchLogger.info(`Successfully extracted chartCode from login cookie: ${memberChartCode}.`);
    window.sc3WorkbenchLogger.info('Attempting to fetch member data and default ChartStyle...');

    const { memberData, defaultChartStyle } = await fetchMemberDataAndDefaultChartStyle({ chartCode: memberChartCode });

    rawMemberData.value = memberData;

    if (defaultChartStyle) {
      savedChartsStore.$patch({ chartStyles: [defaultChartStyle] });
    }

    const defaultChartStyleRecid = defaultChartStyle?.recid ?? INVALID_CHART_STYLE_RECID;

    savedChartsStore.$patch({ recidOfDefaultChartStyle: defaultChartStyleRecid });

    hasOptionsPlayAddOn.value = doesMemberHaveOptionsPlayAddOn(loginCookie);

    window.sc3WorkbenchLogger.info(`Successfully fetched member data and default ChartStyle (recid: ${defaultChartStyleRecid}). User is now ${isLoggedIn.value ? 'logged-in' : 'logged-out'}.`);
  } catch (e) {
    window.sc3WorkbenchLogger.error('Error occurred while authenticating member via fetching login data. User remains logged-out.', e);

    resetAuthenticationData();
  }

  isAuthenticatingMember.value = false;
  isMemberAuthenticationComplete.value = true;

  while (memberAuthenticationCallbacks.length) {
    const memberAuthenticationCallback = memberAuthenticationCallbacks.shift();

    memberAuthenticationCallback();
  }
}

function addMemberAuthenticationCallback(callback) {
  if (isMemberAuthenticationComplete.value) {
    callback();
    return;
  }

  memberAuthenticationCallbacks.push(callback);
}

async function refreshUserLoginCookies() {
  if (!isLoggedIn.value) {
    return;
  }

  if (!hasLoginCookie()) {
    window.location.reload(true);
    return;
  }

  try {
    const loginResponseJson = await loginMember();

    if (!loginResponseJson.success) {
      window.location.reload(true);
      return;
    }

    const loginCookie = loginResponseJson.loginCookie;

    if (typeof loginCookie !== 'string') {
      throw new Error(`Failed to re-login member - loginResponseJson.loginCookie is not a string: ${typeof loginCookie}.`);
    }

    if (!loginCookie.trim()) {
      throw new Error('Failed to re-login member - trimmed loginResponseJson.loginCookie is an empty string.');
    }

    window.sc3WorkbenchLogger.info('Successfully confirmed that member is (still) logged-in.');
    window.sc3WorkbenchLogger.info(`Attempting to extract chartCode from (new) login cookie: ${loginCookie}...`);

    const newChartCode = extractChartCodeFromLoginCookie(loginCookie);

    window.sc3WorkbenchLogger.info(`Successfully extracted chartCode from (new) login cookie: ${newChartCode}.`);

    if (newChartCode !== chartCode.value) {
      window.location.reload(true);
    }
  } catch (error) {
    window.sc3WorkbenchLogger.error('Error occurred while refreshing user login cookies. User remains logged-in.', error);
  }
}

async function init() {
  if (hasInitialized) {
    return;
  }

  hasInitialized = true;

  addMemberAuthenticationCallback(async () => {
    const THIRTY_SECONDS = 30 * 1000;

    await wait(THIRTY_SECONDS);

    if (!isLoggedIn.value) {
      return;
    }

    const throttledRefreshUserLoginCookies = throttle(refreshUserLoginCookies, THIRTY_SECONDS, false);

    async function handleUserInteraction(event) {
      if (!isInteractiveHtmlElement(event.target)) {
        return;
      }

      throttledRefreshUserLoginCookies();
    }

    document.body.addEventListener('keydown', handleUserInteraction);
    document.body.addEventListener('keyup', handleUserInteraction);
    document.body.addEventListener('mousedown', handleUserInteraction);
    document.body.addEventListener('mouseup', handleUserInteraction);
    document.body.addEventListener('click', handleUserInteraction);
    document.body.addEventListener('dblclick', handleUserInteraction);
    document.body.addEventListener('contextmenu', handleUserInteraction);
    document.body.addEventListener('touchstart', handleUserInteraction);
    document.body.addEventListener('touchend', handleUserInteraction);
    document.body.addEventListener('touchmove', handleUserInteraction);
    document.body.addEventListener('touchcancel', handleUserInteraction);
  });

  await authenticateMember();
}

export function useAuthentication() {
  init();

  return {
    hasOptionsPlayAddOn: readonly(hasOptionsPlayAddOn),
    isLoggedIn,
    chartCode,
    memberName,
    memberLastName,
    memberFullName,
    memberEmail,
    memberType,
    memberRT,
    isBasic,
    isExtra,
    isPro,
    addMemberAuthenticationCallback,
    isAuthenticatingMember: readonly(isAuthenticatingMember),
    isMemberAuthenticationComplete: readonly(isMemberAuthenticationComplete)
  };
}
